import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import PageHeader from '../../shared/page-header/PageHeader';
import DataTable from 'react-data-table-component';
import * as Icons from 'react-feather';
import { Link } from 'react-router-dom';
import { ReactComponent as SortArrow } from '../../shared/img/chevron-up-down.svg';
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { getModulePermissions } from '../../utils/permissionHelper';
import { logAuditEvent } from '../../utils/auditLogger';
import { getStorageValues } from '../../shared/CommonFunctions';
import MasterValueModal from './MasterValueModal';

function MasterDataMain(props) {
  const [types, setTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [selectedTypeName, setSelectedTypeName] = useState('');
  const [values, setValues] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingValues, setLoadingValues] = useState(false);
  const [alert, setAlert] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editValue, setEditValue] = useState(null);
  const [userModulePermission, setUserModulePermission] = useState({});
  const moduleName = props.Module || 'Manage Master Data';

  const fetchValues = useCallback(async (typeId) => {
    if (!typeId) return;
    try {
      setLoadingValues(true);
      const response = await axios.get(`${getApiBaseUrl()}/master/types/${typeId}/values`, { params: { includeInactive: true } });
      setValues(response.data?.data || []);
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to load values', 'Error', setAlert);
    } finally {
      setLoadingValues(false);
    }
  }, []);

  const handleSelectType = useCallback((type) => {
    setSelectedTypeId(type.MasterTypeId);
    setSelectedTypeName(type.TypeName);
    fetchValues(type.MasterTypeId);
  }, [fetchValues]);

  const fetchTypes = useCallback(async () => {
    try {
      setLoadingTypes(true);
      const applicationId = process.env.REACT_APP_APPLICATION_ID;
      const response = await axios.get(`${getApiBaseUrl()}/master/types`, { 
        params: { 
          includeInactive: true,
          ApplicationId: applicationId 
        } 
      });
      const data = response.data?.data || [];
      setTypes(data);
      if (!selectedTypeId && data.length > 0) {
        handleSelectType(data[0]);
      }
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to load master types', 'Error', setAlert);
    } finally {
      setLoadingTypes(false);
    }
  }, [selectedTypeId, handleSelectType]);

  useEffect(() => {
    const perms = getModulePermissions(props.Module);
    setUserModulePermission(perms ? perms : []);
    fetchTypes();
    logAuditEvent(moduleName, 'Accessed Module', 'Accessed Manage Master Data');
  }, [props.Module, fetchTypes, moduleName]);

  const openAddModal = () => {
    setEditValue(null);
    setModalOpen(true);
  };
  const openEditModal = (row) => {
    setEditValue(row);
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);

  const onSubmit = async (form) => {
    if (!selectedTypeId) {
      CreateAlert('Select a master type first', 'Warning', setAlert);
      return;
    }
    try {
      const userId = getStorageValues('userId') || 1;
      if (editValue) {
        const payload = {
          ValueKey: form.ValueKey.trim(),
          ValueName: form.ValueName.trim(),
          DisplayName: form.DisplayName.trim(),
          Description: form.Description || null,
          SortOrder: Number(form.SortOrder) || 0,
          IsActive: !!form.IsActive,
          UserId: userId
        };
        const response = await axios.put(`${getApiBaseUrl()}/master/values/${editValue.MasterValueId}`, payload);
        if (response.status === 200) {
          CreateAlert('Value updated successfully', 'Success', setAlert);
          fetchValues(selectedTypeId);
          closeModal();
        }
      } else {
        const payload = {
          ValueKey: form.ValueKey.trim(),
          ValueName: form.ValueName.trim(),
          DisplayName: form.DisplayName.trim(),
          Description: form.Description || null,
          SortOrder: Number(form.SortOrder) || 0,
          IsActive: !!form.IsActive,
          UserId: userId
        };
        const response = await axios.post(`${getApiBaseUrl()}/master/types/${selectedTypeId}/values`, payload);
        if (response.status === 201 || response.status === 200) {
          CreateAlert('Value added successfully', 'Success', setAlert);
          fetchValues(selectedTypeId);
          closeModal();
        }
      }
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 409) {
        CreateAlert('Duplicate key for this type', 'Error', setAlert);
      } else {
        CreateAlert('Save failed', 'Error', setAlert);
      }
    }
  };

  const toggleActive = useCallback(async (row) => {
    try {
      const userId = getStorageValues('userId') || 1;
      const next = !(row.IsActive === 1 || row.IsActive === true);
      await axios.delete(`${getApiBaseUrl()}/master/values/${row.MasterValueId}`, { data: { IsActive: next, UserId: userId } });
      fetchValues(selectedTypeId);
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to update status', 'Error', setAlert);
    }
  }, [fetchValues, selectedTypeId]);


  const renderTypes = useMemo(() => {
    if (loadingTypes) return <div className="p-3">Loading types...</div>;
    if (!types.length) return <div className="p-3">No master types</div>;
    return (
      <ul className="list-group">
        {types.map((t) => (
          <li key={t.MasterTypeId} className={`list-group-item d-flex justify-content-between align-items-center ${selectedTypeId === t.MasterTypeId ? 'active' : ''}`} style={{ cursor: 'pointer' }} onClick={() => handleSelectType(t)}>
            <span>{t.TypeName}</span>
            <span className={`badge ${t.IsActive ? 'bg-success' : 'bg-secondary'}`}>{t.IsActive ? 'Active' : 'Inactive'}</span>
          </li>
        ))}
      </ul>
    );
  }, [types, selectedTypeId, loadingTypes, handleSelectType]);

  const gridColumns = useMemo(() => ([
    {
      name: 'Name',
      selector: row => row.ValueName,
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: 'Display',
      selector: row => row.DisplayName,
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: 'Description',
      selector: row => row.Description,
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: 'Sort',
      selector: row => row.SortOrder,
      sortable: true,
      sortIcon: <SortArrow />,
      width: '90px'
    },
    {
      name: 'Active',
      cell: (row) => (
        <span className={`badge ${row.IsActive ? 'bg-success' : 'bg-secondary'}`}>{row.IsActive ? 'Yes' : 'No'}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />,
      width: '110px'
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="edit-delete-action">
          {userModulePermission.EditModule ? (
            <>
              <Link
                className="me-2 p-2"
                to=""
                onClick={(e) => {
                  e.preventDefault();
                  openEditModal(row);
                }}
              >
                <Icons.Edit data-feather="edit" className="feather-edit" />
              </Link>
              <Link
                className="me-2 p-2"
                to=""
                onClick={(e) => {
                  e.preventDefault();
                  toggleActive(row);
                }}
                title={row.IsActive ? 'Deactivate' : 'Activate'}
              >
                <Icons.Trash2 data-feather="trash-2" className="feather-trash-2" />
              </Link>
            </>
          ) : null}
        </div>
      )
    }
  ]), [userModulePermission, toggleActive]);

  const renderGrid = useMemo(() => {
    if (loadingValues) return <div className="p-3">Loading values...</div>;
    return (
      <DataTable
        className="table-responsive product-list"
        columns={gridColumns}
        data={values}
        pagination
        defaultSortField="ValueName"
        sortIcon={<SortArrow />}
        persistTableHead
        striped
        noDataComponent={<div className="p-3 text-center">No values</div>}
      />
    );
  }, [values, loadingValues, gridColumns]);

  const handleRefresh = () => {
    if (selectedTypeId) fetchValues(selectedTypeId);
    else fetchTypes();
  };

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={moduleName}
        onModelOpen={openAddModal}
        onRefresh={handleRefresh}
        showPDF={false}
        showExcel={false}
        showPrint={false}
        showRefresh={true}
        showAddButton={userModulePermission.AddModule === 1}
      />
      <div className="row p-2">
        <div className="col-md-3">
          <div className="card">
            <div className="card-header">Master Types</div>
            <div className="card-body" style={{ maxHeight: 500, overflow: 'auto' }}>
              {renderTypes}
            </div>
          </div>
        </div>
        <div className="col-md-9">
          <div className="card">
            <div className="card-header">{selectedTypeName || 'Values'}</div>
            <div className="card-body">
              {renderGrid}
            </div>
          </div>
        </div>
      </div>
      <MasterValueModal
        show={modalOpen}
        onClose={closeModal}
        onSubmit={onSubmit}
        initialValue={editValue}
        typeName={selectedTypeName}
      />
    </>
  );
}

export default MasterDataMain;
